/**
 * Greeting Utilities
 *
 * Time-aware greeting messages based on client's local time.
 *
 * Time boundaries:
 * - Morning: 05:00-11:59
 * - Afternoon: 12:00-16:59
 * - Evening: 17:00-04:59
 *
 * @module utils/greeting
 */

/**
 * Time of day categories for greetings.
 */
export type TimeOfDay = 'morning' | 'afternoon' | 'evening';

/**
 * Get time-of-day category based on client's local time.
 *
 * Uses the following boundaries:
 * - Morning: 5 AM - 12 PM (05:00-11:59)
 * - Afternoon: 12 PM - 5 PM (12:00-16:59)
 * - Evening: 5 PM - 5 AM (17:00-04:59)
 *
 * @returns Time of day category
 *
 * @example
 * // At 9:00 AM:
 * getTimeGreeting() // 'morning'
 *
 * // At 2:00 PM:
 * getTimeGreeting() // 'afternoon'
 *
 * // At 8:00 PM:
 * getTimeGreeting() // 'evening'
 */
export function getTimeGreeting(): TimeOfDay {
  const hour = new Date().getHours();

  if (hour >= 5 && hour < 12) {
    return 'morning';
  }

  if (hour >= 12 && hour < 17) {
    return 'afternoon';
  }

  return 'evening';
}

/**
 * Format personalized greeting with time of day.
 *
 * @param firstName - User's first name to include in greeting
 * @returns Formatted greeting string
 *
 * @example
 * // At 9:00 AM:
 * formatGreeting('Alice') // 'Good morning, Alice'
 *
 * // At 2:00 PM:
 * formatGreeting('Bob') // 'Good afternoon, Bob'
 */
export function formatGreeting(firstName: string): string {
  const time = getTimeGreeting();

  const prefix: Record<TimeOfDay, string> = {
    morning: 'Good morning',
    afternoon: 'Good afternoon',
    evening: 'Good evening',
  };

  return `${prefix[time]}, ${firstName}`;
}
